import {
  createFileRoute,
  Link,
  Outlet,
  useMatches,
  useNavigate,
} from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState } from 'react';

import { Button, Card, ConfirmDialog, EmptyState, Spinner } from '@/components';
import type { VillageMapListItem, VillageMapRecord } from '@/lib/admin-types';
import { apiJson } from '@/lib/api';
import { formatAdminDateTime } from '@/lib/format';

function VillageMapsList() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const listQ = useQuery({
    queryKey: ['admin', 'village-maps'],
    queryFn: () => apiJson<VillageMapListItem[]>('/v1/admin/village-maps'),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      apiJson<VillageMapRecord>('/v1/admin/village-maps', {
        method: 'POST',
        body: JSON.stringify({ name: 'Untitled village maps' }),
      }),
    onSuccess: (row) => {
      void qc.invalidateQueries({ queryKey: ['admin', 'village-maps'] });
      void navigate({
        to: '/admin/village-maps/$id',
        params: { id: String(row.id) },
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) =>
      apiJson<void>(`/v1/admin/village-maps/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setDeleteId(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'village-maps'] });
    },
  });

  if (listQ.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }
  if (listQ.isError) {
    return (
      <p className="text-sm text-red-600">{(listQ.error as Error).message}</p>
    );
  }

  const rows = listQ.data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Village Maps</h1>
        <p className="mt-1 text-sm text-gray-600">
          Design outdoor maps here, Save, then Export JSON and hand it to a Cursor
          agent to apply to the mobile repo.
        </p>
      </div>

      <Card className="space-y-3">
        <div className="flex flex-wrap items-end gap-2">
          <Button
            disabled={createMutation.isPending}
            loading={createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            Create map
          </Button>
        </div>
        <p className="text-xs text-gray-500">
          New maps start from the seed outdoor layouts (town, farm, mountain).
          Export does not publish to the app — a Cursor agent applies the JSON to{' '}
          <code className="text-gray-600">villageOutdoorMaps.ts</code>.
        </p>
        {createMutation.isError ? (
          <p className="text-sm text-red-600">
            {(createMutation.error as Error).message}
          </p>
        ) : null}
      </Card>

      {rows.length === 0 ? (
        <EmptyState title="No village maps yet" />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map((row) => (
            <Card key={row.id} className="relative space-y-2">
              <Link
                to="/admin/village-maps/$id"
                params={{ id: String(row.id) }}
                className={clsx(
                  'group block rounded-[22px] focus:outline-none',
                  'focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                )}
              >
                <h2 className="text-lg font-medium text-gray-900 group-hover:text-primary">
                  {row.name}
                </h2>
                {row.notes ? (
                  <p className="mt-1 line-clamp-2 text-sm text-gray-600">{row.notes}</p>
                ) : null}
                <p className="mt-2 text-sm text-gray-500">
                  Updated {formatAdminDateTime(row.updated_at)}
                </p>
                <p className="mt-3 text-sm font-medium text-primary group-hover:underline">
                  Open editor →
                </p>
              </Link>
              <div className="pt-1">
                <Button
                  type="button"
                  size="sm"
                  variant="danger"
                  onClick={() => setDeleteId(row.id)}
                >
                  Delete
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={deleteId != null}
        title="Delete village map?"
        message="This permanently deletes the saved map document. Exports already downloaded are unaffected."
        confirmLabel="Delete"
        confirmVariant="danger"
        loading={deleteMutation.isPending}
        onCancel={() => {
          if (!deleteMutation.isPending) {
            setDeleteId(null);
            deleteMutation.reset();
          }
        }}
        onConfirm={() => {
          if (deleteId != null) deleteMutation.mutate(deleteId);
        }}
      />
      {deleteMutation.isError ? (
        <p className="text-sm text-red-600">
          {(deleteMutation.error as Error).message}
        </p>
      ) : null}
    </div>
  );
}

export const Route = createFileRoute('/admin/village-maps')({
  component: function VillageMapsRoute() {
    const matches = useMatches();
    const onDetail = matches.some((m) => m.routeId.includes('village-maps/$id'));
    if (onDetail) return <Outlet />;
    return <VillageMapsList />;
  },
});
